import { computed, nextTick } from 'vue'
import { nanoid } from 'nanoid'
import { getOrCreateChatSessionStore } from '@/stores/chat-session'
import { getOrCreateChatbotStore } from '@/stores/chatbot'
import { ChatSessionApi } from '@/api'
import { useAuth } from './useAuth'
import { useChatbotContext } from './useChatbotContext'
import { useAnalytics } from './useAnalytics'
import type { IChatMessage } from '@/types/chat-session-types'
import type {
  IChatSessionWithState,
  IChatMessageWithState,
} from '@/stores/chat-session/types'
import { logger } from '@/utils/logger'
import {
  extractAnswerFromStreamingJson,
  JSON_ANSWER_START_REGEX,
} from '@/utils/text'
import {
  parseStreamChunk,
  finalizeStreamResponse,
  type IStreamMetadata,
} from '@/utils/stream-parser'
import { getRecentConversationHistory } from '@/utils/conversation'
import {
  WIDGET_CONSTANTS,
  MESSAGE_CONSTANTS,
  SESSION_ERROR_MESSAGES,
} from '@/types/constants'
import {
  ANALYTICS_EVENTS,
  ANALYTICS_SOURCE,
  SESSION_RECOVERY_REASONS,
  type QuestionType,
  type IAnalyticsEventPayload,
} from '@/types/analytics-types'

interface IUseChatSessionOptions {
  persistMessages?: boolean
  autoScrollToBottom?: boolean
}

const DEFAULT_OPTIONS: Required<IUseChatSessionOptions> = {
  persistMessages: true,
  autoScrollToBottom: true,
}

const MAX_SESSION_RECOVERY_RETRIES = 3

export function useChatSession(options: IUseChatSessionOptions = {}) {
  const { autoScrollToBottom } = { ...DEFAULT_OPTIONS, ...options }

  const context = useChatbotContext()
  if (!context) {
    throw new Error(
      'Chatbot context must be provided before using useChatSession'
    )
  }
  const scopedClient = context.apiClient
  const storeKey = context.storeKey.value

  if (!storeKey) {
    throw new Error('Store key must be set before using useChatSession')
  }

  const auth = useAuth()
  const analytics = useAnalytics()

  const chatSessionStoreFactory = getOrCreateChatSessionStore(storeKey)
  const storeInstance = chatSessionStoreFactory()
  const store = storeInstance

  const chatbotStoreFactory = getOrCreateChatbotStore(storeKey)
  const chatbotStore = chatbotStoreFactory()

  const isReady = computed(
    () => storeInstance.isInitialized && !storeInstance.isLoading
  )
  const hasError = computed(() => storeInstance.error !== null)
  const canSendMessage = computed(
    () => !storeInstance.isTyping && !storeInstance.isLoading
  )

  const generateMessageId = (): string => {
    return nanoid()
  }

  let messageContainerCache: Element | null = null

  const getMessageContainer = (): Element | null => {
    if (!messageContainerCache) {
      messageContainerCache = document.querySelector(
        `[${WIDGET_CONSTANTS.DATA_ATTRIBUTES.CHAT_MESSAGES}]`
      )
    }
    return messageContainerCache
  }

  const scrollToBottom = async (): Promise<void> => {
    if (!autoScrollToBottom) return

    await nextTick()
    const messageContainer = getMessageContainer()
    if (messageContainer) {
      messageContainer.scrollTop = messageContainer.scrollHeight
    }
  }

  const validatePersistedSession = async (
    sessionId: string
  ): Promise<boolean> => {
    try {
      const response = await ChatSessionApi.getChatSession(
        sessionId,
        {
          chatbotId: chatbotStore.id || undefined,
          tenantId: chatbotStore.tenantId || auth.tenantId.value || undefined,
        },
        scopedClient
      )
      return response.success && !!response.data
    } catch {
      return false
    }
  }

  const createNewSession = async (chatbotId: string): Promise<string> => {
    const response = await ChatSessionApi.startChatSession(
      {
        chatbotId,
        userId: auth.userId.value || undefined,
        tenantId: chatbotStore.tenantId || auth.tenantId.value || undefined,
      },
      scopedClient
    )

    if (response.success && response.data) {
      const sessionData = response.data

      const newSession: IChatSessionWithState = {
        ...sessionData,
        messages: [],
        createdAt: new Date(),
        lastActivity: new Date(),
        isActive: true,
      }

      storeInstance.setSession(newSession)
      storeInstance.setActiveSession(sessionData.sessionId)
      storeInstance.setConnected(true)
      logger.info(
        'Chat session started',
        { sessionId: sessionData.sessionId },
        { component: 'useChatSession' }
      )

      return sessionData.sessionId
    }

    throw new Error(response.message || 'Failed to start chat session')
  }

  const startSession = async (chatbotId: string): Promise<string> => {
    if (storeInstance.isCreatingSession) {
      logger.warn(
        'Session creation already in progress, skipping duplicate request',
        { chatbotId },
        { component: 'useChatSession' }
      )
      return ''
    }

    if (storeInstance.activeSessionId) {
      const isValid = await validatePersistedSession(
        storeInstance.activeSessionId
      )
      if (isValid) {
        storeInstance.setConnected(true)
        return storeInstance.activeSessionId
      }

      logger.warn(
        'Persisted session is no longer valid, creating new session',
        { oldSessionId: storeInstance.activeSessionId },
        { component: 'useChatSession' }
      )
      storeInstance.markSessionInactive(storeInstance.activeSessionId)
    }

    storeInstance.setLoading(true)
    storeInstance.setCreatingSession(true)
    storeInstance.setError(null)

    try {
      return await createNewSession(chatbotId)
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to start chat session'

      logger.error(
        'Failed to start chat session',
        error,
        { component: 'useChatSession' },
        error instanceof Error ? error : undefined
      )
      storeInstance.setError(errorMessage)
      storeInstance.setConnected(false)

      return ''
    } finally {
      storeInstance.setLoading(false)
      storeInstance.setCreatingSession(false)
    }
  }

  const sendMessage = async (
    content: string,
    isSuggestedQuestion: boolean = false,
    isFollowUpQuestion: boolean = false,
    parentMessageId?: string,
    retryCount: number = 0
  ): Promise<IChatMessage | null> => {
    if (!canSendMessage.value || !content.trim()) {
      return null
    }

    let currentSessionId = storeInstance.activeSessionId
    if (!currentSessionId && chatbotStore.id) {
      logger.info(
        'No active session, creating one automatically',
        {},
        { component: 'useChatSession' }
      )
      currentSessionId = await startSession(chatbotStore.id)
    }
    if (!currentSessionId) {
      storeInstance.setError('No active chat session')
      return null
    }

    const userMessageId = generateMessageId()

    let questionType: QuestionType = 'typed'
    if (isFollowUpQuestion) {
      questionType = 'followUp'
    } else if (isSuggestedQuestion) {
      questionType = 'suggested'
    }

    analytics.trackEvent({
      eventName: ANALYTICS_EVENTS.QUERY_SUBMITTED,
      timestamp: new Date().toISOString(),
      payload: {
        characterCount: content.trim().length,
        isSuggestedQuestion: isSuggestedQuestion,
        isFollowUpQuestion: isFollowUpQuestion,
        questionType: questionType,
        query: content.trim(),
        messageId: userMessageId,
        parentMessageId: parentMessageId,
      },
      source: ANALYTICS_SOURCE,
      sessionId: currentSessionId,
      userId: auth.userId.value || undefined,
      chatbotId: chatbotStore.id || undefined,
      tenant: chatbotStore.tenantId || undefined,
    })

    const userMessage: IChatMessageWithState = {
      id: userMessageId,
      userMessage: content.trim(),
      answer: '',
      reasoning: undefined,
      answerFound: false,
      referenceNotes: [],
      sourceDocuments: [],
      followUpQuestions: [],
      success: true,
      timestamp: new Date().toISOString(),
      sessionId: currentSessionId,
      status: MESSAGE_CONSTANTS.STATUS.SENT,
    }

    storeInstance.addMessageToActiveSession(userMessage)
    storeInstance.setTyping(true)
    storeInstance.setError(null)

    try {
      const result = await trySendMessage(
        content.trim(),
        currentSessionId,
        userMessageId
      )

      if (result.success && result.data) {
        storeInstance.setConnected(true)

        if (result.data.answerFound) {
          const sourceDocsForAnalytics = (
            result.data.sourceDocuments || []
          ).map(doc => doc.url || doc.name)
          analytics.trackEvent({
            eventName: ANALYTICS_EVENTS.ANSWER_GENERATED,
            timestamp: new Date().toISOString(),
            payload: {
              characterCount: result.data.answer?.length || 0,
              sourceDocuments: sourceDocsForAnalytics,
              referenceNotes: result.data.referenceNotes || [],
              queryResponse: result.data.answer,
              messageId: userMessageId,
              usedConversationHistory:
                result.data.usedConversationHistory ?? false,
            },
            source: ANALYTICS_SOURCE,
            sessionId: currentSessionId,
            userId: auth.userId.value || undefined,
            chatbotId: chatbotStore.id || undefined,
            tenant: chatbotStore.tenantId || undefined,
          })
        } else {
          analytics.trackEvent({
            eventName: ANALYTICS_EVENTS.NO_RESPONSE_GENERATED,
            timestamp: new Date().toISOString(),
            payload: {
              query: content.trim(),
              messageId: userMessageId,
            },
            source: ANALYTICS_SOURCE,
            sessionId: currentSessionId,
            userId: auth.userId.value || undefined,
            chatbotId: chatbotStore.id || undefined,
            tenant: chatbotStore.tenantId || undefined,
          })
        }

        return result.data
      } else {
        if (
          result.isSessionInactive &&
          retryCount < MAX_SESSION_RECOVERY_RETRIES &&
          chatbotStore.id
        ) {
          return await handleInactiveSessionRecovery(
            currentSessionId,
            content,
            isSuggestedQuestion,
            isFollowUpQuestion,
            parentMessageId,
            retryCount
          )
        }

        storeInstance.setError(result.error || 'Failed to send message')
        storeInstance.setConnected(false)
        return null
      }
    } finally {
      storeInstance.setTyping(false)
    }
  }

  const retryMessage = async (
    failedMessageId: string
  ): Promise<IChatMessage | null> => {
    const currentSessionId = storeInstance.activeSessionId
    if (!currentSessionId) {
      storeInstance.setError('No active chat session')
      return null
    }

    const session = storeInstance.sessions[currentSessionId]
    if (!session) {
      storeInstance.setError('Session not found')
      return null
    }

    const failedMessageIndex = session.messages.findIndex(
      msg => msg.id === failedMessageId
    )
    const failedMessage = session.messages[failedMessageIndex]

    if (
      !failedMessage ||
      !failedMessage.replyToId ||
      failedMessageIndex === -1
    ) {
      storeInstance.setError(
        'Failed message or original user message not found'
      )
      return null
    }

    const originalUserMessage = session.messages.find(
      msg => msg.id === failedMessage.replyToId
    )
    if (!originalUserMessage || !originalUserMessage.userMessage) {
      storeInstance.setError('Original user message not found')
      return null
    }

    storeInstance.setTyping(true)
    storeInstance.setError(null)

    try {
      const result = await trySendMessage(
        originalUserMessage.userMessage,
        currentSessionId,
        failedMessage.replyToId,
        failedMessageId
      )

      if (result.success && result.data) {
        const successMessage = createAiResponseMessage(
          result.data,
          currentSessionId,
          failedMessage.replyToId,
          failedMessageId
        )
        session.messages[failedMessageIndex] = successMessage
        storeInstance.setConnected(true)
        return result.data
      } else {
        const updatedErrorMessage = createErrorMessage(
          result.error || 'Failed to retry message',
          currentSessionId,
          failedMessage.replyToId,
          failedMessageId
        )
        session.messages[failedMessageIndex] = updatedErrorMessage
        storeInstance.setError(result.error || 'Failed to retry message')
        storeInstance.setConnected(false)
        return null
      }
    } finally {
      storeInstance.setTyping(false)
    }
  }

  const switchSession = (sessionId: string): boolean => {
    return storeInstance.setActiveSession(sessionId)
  }

  const isSessionInactiveError = (errorMessage: string): boolean => {
    return (
      errorMessage === SESSION_ERROR_MESSAGES.SESSION_NOT_ACTIVE ||
      errorMessage === SESSION_ERROR_MESSAGES.SESSION_NOT_FOUND
    )
  }

  const handleInactiveSessionRecovery = async (
    oldSessionId: string,
    content: string,
    isSuggestedQuestion: boolean,
    isFollowUpQuestion: boolean,
    parentMessageId?: string,
    retryCount: number = 0
  ): Promise<IChatMessage | null> => {
    logger.warn(
      'Session inactive, creating new session',
      { oldSessionId },
      { component: 'useChatSession' }
    )

    analytics.trackEvent({
      eventName: ANALYTICS_EVENTS.SESSION_RECOVERY_ATTEMPTED,
      timestamp: new Date().toISOString(),
      payload: {
        oldSessionId,
        reason: SESSION_RECOVERY_REASONS.SESSION_INACTIVE,
        hadParentMessage: !!parentMessageId,
      },
      source: ANALYTICS_SOURCE,
      sessionId: oldSessionId,
      userId: auth.userId.value || undefined,
      chatbotId: chatbotStore.id || undefined,
      tenant: chatbotStore.tenantId || undefined,
    })

    storeInstance.markSessionInactive(oldSessionId)

    if (!chatbotStore.id) {
      storeInstance.setError('Cannot recover session: chatbot ID not available')
      analytics.trackEvent({
        eventName: ANALYTICS_EVENTS.SESSION_RECOVERY_FAILED,
        timestamp: new Date().toISOString(),
        payload: {
          oldSessionId,
          reason: SESSION_RECOVERY_REASONS.NO_CHATBOT_ID,
        },
        source: ANALYTICS_SOURCE,
        userId: auth.userId.value || undefined,
        tenant: chatbotStore.tenantId || undefined,
      })
      return null
    }

    const newSessionId = await startSession(chatbotStore.id)
    if (!newSessionId) {
      storeInstance.setError('Failed to create new session')
      analytics.trackEvent({
        eventName: ANALYTICS_EVENTS.SESSION_RECOVERY_FAILED,
        timestamp: new Date().toISOString(),
        payload: {
          oldSessionId,
          reason: SESSION_RECOVERY_REASONS.SESSION_CREATION_FAILED,
        },
        source: ANALYTICS_SOURCE,
        userId: auth.userId.value || undefined,
        chatbotId: chatbotStore.id || undefined,
        tenant: chatbotStore.tenantId || undefined,
      })
      return null
    }

    analytics.trackEvent({
      eventName: ANALYTICS_EVENTS.SESSION_RECOVERY_SUCCEEDED,
      timestamp: new Date().toISOString(),
      payload: {
        oldSessionId,
        newSessionId,
      },
      source: ANALYTICS_SOURCE,
      sessionId: newSessionId,
      userId: auth.userId.value || undefined,
      chatbotId: chatbotStore.id || undefined,
      tenant: chatbotStore.tenantId || undefined,
    })

    // Don't preserve parentMessageId across session boundaries
    return await sendMessage(
      content,
      isSuggestedQuestion,
      isFollowUpQuestion,
      undefined,
      retryCount + 1
    )
  }

  const clearActiveSessionMessages = (): void => {
    if (storeInstance.activeSessionId) {
      storeInstance.clearSessionMessages(storeInstance.activeSessionId)
    }
  }

  const endSession = async (): Promise<void> => {
    const sessionId = storeInstance.activeSessionId
    if (!sessionId) return

    try {
      await ChatSessionApi.endChatSession(sessionId, scopedClient)
      logger.info(
        'Chat session ended',
        { sessionId },
        { component: 'useChatSession' }
      )
    } catch (error) {
      logger.warn('Failed to end chat session gracefully', error, {
        component: 'useChatSession',
      })
    } finally {
      storeInstance.markSessionInactive(sessionId)
      storeInstance.setConnected(false)
      storeInstance.setError(null)
      storeInstance.setTyping(false)
    }
  }

  const cleanup = (): void => {
    storeInstance.setConnected(false)
    storeInstance.setTyping(false)
    storeInstance.setError(null)
  }

  const createInitialAiMessage = (
    sessionId: string,
    replyToId: string,
    aiMessageId: string
  ): void => {
    const tempAiMessage: IChatMessageWithState = {
      id: aiMessageId,
      userMessage: '',
      answer: '',
      reasoning: undefined,
      answerFound: false,
      referenceNotes: [],
      sourceDocuments: [],
      followUpQuestions: [],
      success: true,
      timestamp: new Date().toISOString(),
      sessionId,
      status: MESSAGE_CONSTANTS.STATUS.SENDING,
      replyToId,
    }
    storeInstance.addMessageToActiveSession(tempAiMessage)
    storeInstance.setTyping(false)
  }

  const trackStreamMetadataAnalytics = (params: {
    eventName: string
    sessionId: string
    payload: IAnalyticsEventPayload
  }): void => {
    analytics.trackEvent({
      eventName: params.eventName,
      timestamp: new Date().toISOString(),
      payload: params.payload,
      source: ANALYTICS_SOURCE,
      sessionId: params.sessionId,
      userId: auth.userId.value || undefined,
      chatbotId: chatbotStore.id || undefined,
      tenant: chatbotStore.tenantId || undefined,
    })
  }

  const handleStreamError = (
    errorText: string,
    aiMessageCreated: boolean,
    sessionId: string,
    aiMessageId: string,
    replyToId: string
  ): void => {
    const displayError =
      errorText || 'Sorry, I encountered an error processing your message.'

    if (!aiMessageCreated) {
      storeInstance.addMessageToActiveSession(
        createErrorMessage(displayError, sessionId, replyToId, aiMessageId)
      )
      storeInstance.setTyping(false)
    } else {
      storeInstance.updateMessageById(sessionId, aiMessageId, {
        answer: displayError,
        success: false,
        error: errorText,
        status: MESSAGE_CONSTANTS.STATUS.FAILED,
      })
    }
  }

  const trySendMessage = async (
    userMessage: string,
    sessionId: string,
    replyToId: string,
    messageIdOverride?: string
  ): Promise<{
    success: boolean
    data?: IChatMessage
    error?: string
    isSessionInactive?: boolean
  }> => {
    return new Promise(resolve => {
      let accumulatedJsonContent = ''
      let extractedAnswerSoFar = ''
      let isExtractingAnswer = false
      let hasError = false
      let errorMessage = ''
      let isSessionInactive = false
      let aiMessageCreated = false
      const streamMetadata: IStreamMetadata = {}

      const aiMessageId = messageIdOverride || generateMessageId()

      const session = storeInstance.sessions[sessionId]
      const messagesExcludingCurrent = (session?.messages || []).filter(
        msg => msg.id !== replyToId
      )
      const conversationHistory =
        messagesExcludingCurrent.length > 0
          ? getRecentConversationHistory(messagesExcludingCurrent)
          : []

      ChatSessionApi.sendMessage(
        {
          message: userMessage,
          sessionId,
          userId: auth.userId.value || undefined,
          chatbotId: chatbotStore.id || undefined,
          tenantId: chatbotStore.tenantId || auth.tenantId.value || undefined,
          conversationHistory:
            conversationHistory.length > 0 ? conversationHistory : undefined,
        },
        // onChunk
        (chunk: string) => {
          const parsed = parseStreamChunk(chunk)

          if (parsed.type === 'done') return

          if (parsed.type === 'error') {
            hasError = true
            errorMessage = parsed.error || 'Unknown error occurred'
            isSessionInactive = isSessionInactiveError(errorMessage)
            return
          }

          // Handle content accumulation and progressive answer extraction
          if (parsed.content !== undefined) {
            if (parsed.isReplacement) {
              accumulatedJsonContent = parsed.content
            } else {
              accumulatedJsonContent += parsed.content
            }

            if (!aiMessageCreated) {
              createInitialAiMessage(sessionId, replyToId, aiMessageId)
              aiMessageCreated = true
            }

            if (
              !isExtractingAnswer &&
              JSON_ANSWER_START_REGEX.test(accumulatedJsonContent)
            ) {
              isExtractingAnswer = true
            }
            if (isExtractingAnswer) {
              const currentAnswer = extractAnswerFromStreamingJson(
                accumulatedJsonContent
              )
              if (currentAnswer && currentAnswer !== extractedAnswerSoFar) {
                extractedAnswerSoFar = currentAnswer
                storeInstance.updateMessageById(sessionId, aiMessageId, {
                  answer: extractedAnswerSoFar,
                })
              }
            }
          }

          // Handle metadata fields
          if (parsed.answerFound !== undefined)
            streamMetadata.answerFound = parsed.answerFound
          if (parsed.referenceNotes)
            streamMetadata.referenceNotes = parsed.referenceNotes
          if (parsed.reasoning) streamMetadata.reasoning = parsed.reasoning

          if (parsed.sourceDocuments && aiMessageCreated) {
            streamMetadata.sourceDocuments = parsed.sourceDocuments
            storeInstance.updateMessageById(sessionId, aiMessageId, {
              sourceDocuments: parsed.sourceDocuments,
            })
            trackStreamMetadataAnalytics({
              eventName: ANALYTICS_EVENTS.SOURCE_DOCUMENTS_RECEIVED,
              sessionId,
              payload: {
                messageId: aiMessageId,
                documentCount: parsed.sourceDocuments.length,
                documents: parsed.sourceDocuments.map(doc => ({
                  name: doc.name,
                  hasUrl: !!doc.url,
                })),
                answerLength: extractedAnswerSoFar.length,
              },
            })
          }

          if (parsed.followUpQuestions && aiMessageCreated) {
            streamMetadata.followUpQuestions = parsed.followUpQuestions
            storeInstance.updateMessageById(sessionId, aiMessageId, {
              followUpQuestions: parsed.followUpQuestions,
            })
            trackStreamMetadataAnalytics({
              eventName: ANALYTICS_EVENTS.FOLLOW_UP_QUESTIONS_GENERATED,
              sessionId,
              payload: {
                messageId: aiMessageId,
                questionCount: parsed.followUpQuestions.length,
                questions: parsed.followUpQuestions,
                answerLength: extractedAnswerSoFar.length,
                hasSourceDocuments: !!streamMetadata.sourceDocuments?.length,
              },
            })
          }
        },
        // onComplete
        () => {
          if (hasError) {
            handleStreamError(
              errorMessage,
              aiMessageCreated,
              sessionId,
              aiMessageId,
              replyToId
            )
            resolve({
              success: false,
              error: errorMessage,
              isSessionInactive,
            })
            return
          }

          const finalized = finalizeStreamResponse(
            accumulatedJsonContent,
            streamMetadata
          )

          storeInstance.updateMessageById(sessionId, aiMessageId, {
            reasoning: finalized.reasoning,
            answerFound: finalized.answerFound ?? false,
            referenceNotes: finalized.referenceNotes || [],
            sourceDocuments: finalized.sourceDocuments || [],
            followUpQuestions: streamMetadata.followUpQuestions || [],
            usedConversationHistory: finalized.usedConversationHistory ?? false,
            status: MESSAGE_CONSTANTS.STATUS.SENT,
          })

          resolve({
            success: true,
            data: {
              userMessage: '',
              answer: extractedAnswerSoFar || finalized.answer || '',
              reasoning: finalized.reasoning,
              answerFound: finalized.answerFound ?? false,
              referenceNotes: finalized.referenceNotes || [],
              sourceDocuments: finalized.sourceDocuments || [],
              followUpQuestions: streamMetadata.followUpQuestions || [],
              usedConversationHistory:
                finalized.usedConversationHistory ?? false,
              success: true,
              timestamp: new Date().toISOString(),
              sessionId,
            },
          })
        },
        // onError
        (error: Error) => {
          logger.error(
            'Failed to send message',
            error,
            { component: 'useChatSession' },
            error
          )

          handleStreamError(
            error.message,
            aiMessageCreated,
            sessionId,
            aiMessageId,
            replyToId
          )

          resolve({
            success: false,
            error: error.message,
            isSessionInactive: false,
          })
        },
        scopedClient
      )
    })
  }

  const createAiResponseMessage = (
    messageData: IChatMessage,
    sessionId: string,
    replyToId: string,
    messageId?: string
  ): IChatMessageWithState => {
    return {
      id: messageId || generateMessageId(),
      userMessage: '',
      answer: messageData.answer,
      reasoning: messageData.reasoning,
      answerFound: messageData.answerFound,
      referenceNotes: messageData.referenceNotes,
      sourceDocuments: messageData.sourceDocuments,
      followUpQuestions: messageData.followUpQuestions || [],
      success: messageData.success,
      error: messageData.error,
      timestamp: messageData.timestamp,
      sessionId: sessionId,
      status: MESSAGE_CONSTANTS.STATUS.SENT,
      replyToId: replyToId,
    }
  }

  const createErrorMessage = (
    errorText: string,
    sessionId: string,
    replyToId: string,
    messageId?: string
  ): IChatMessageWithState => {
    return {
      id: messageId || generateMessageId(),
      userMessage: '',
      answer:
        errorText || 'Sorry, I encountered an error processing your message.',
      reasoning: undefined,
      answerFound: false,
      referenceNotes: [],
      sourceDocuments: [],
      followUpQuestions: [],
      success: false,
      error: errorText,
      timestamp: new Date().toISOString(),
      sessionId: sessionId,
      status: MESSAGE_CONSTANTS.STATUS.FAILED,
      replyToId: replyToId,
    }
  }

  return {
    store,
    isReady,
    hasError,
    canSendMessage,

    // Methods
    startSession,
    sendMessage,
    retryMessage,
    switchSession,
    clearMessages: clearActiveSessionMessages,
    endSession,
    cleanup,

    scrollToBottom,
  }
}

export default useChatSession
